import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LoginInput, SessionUser } from "@wcp/shared";
import { ApiError, apiFetch } from "../../lib/api.js";

/** Capabilities payload (doc 13 §2.1) — the SPA renders the AI cascade from this. */
export interface SessionFeatures {
  aiModule: boolean;
}
interface SessionPayload {
  user: SessionUser;
  features: SessionFeatures;
}

function useSession<T>(select: (s: SessionPayload | null) => T) {
  return useQuery({
    queryKey: ["me"],
    queryFn: async (): Promise<SessionPayload | null> => {
      try {
        return await apiFetch<SessionPayload>("/api/v1/auth/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 60_000,
    select,
  });
}

export function useMe() {
  return useSession((s) => s?.user ?? null);
}

export function useFeatures() {
  return useSession((s) => s?.features ?? { aiModule: false });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginInput) =>
      apiFetch<{ user: SessionUser }>("/api/v1/auth/login", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // Refetch the full session (user + features) and AWAIT it before the mutation
    // resolves, so LoginScreen's navigate("/") happens only after ["me"] is
    // authenticated. refetchType "all" forces the refetch even though the query is
    // inactive on /login — otherwise it's merely marked stale and the AppShell guard
    // reads the pre-login cached null and bounces back to /login (the "log in twice"
    // bug). Returning the promise makes mutateAsync await the refetch.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"], refetchType: "all" }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: true }>("/api/v1/auth/logout", { method: "POST" }),
    onSuccess: () => {
      qc.setQueryData(["me"], null);
      qc.clear();
    },
  });
}
